/**
 * Pre-designed 3D vehicle SVG avatars per category.
 * Uses SVG gradients for realistic top-down 3D shading with
 * strong highlights, shadows, reflective glass, and detailed wheels.
 * Uses "FILL" as placeholder for the user's color.
 *
 * Each avatar includes <defs> with category-prefixed gradient IDs
 * to avoid collisions when multiple instances exist on the page.
 */

import type { VehicleCategory } from '../types';

// ── Motorhome: Large boxy RV with cab-over ──────────────────────────
const MOTORHOME_SVG = [
  // Gradient definitions
  '<defs>',
  '<linearGradient id="mh-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
  '</linearGradient>',
  '<linearGradient id="mh-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.9"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.7"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.85"/>',
  '</linearGradient>',
  '<radialGradient id="mh-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#222"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow (offset, soft)
  '<ellipse cx="21" cy="37" rx="12" ry="3" fill="rgba(0,0,0,0.3)"/>',
  // Main body
  '<rect x="8" y="4" width="24" height="32" rx="4" fill="FILL"/>',
  // Cab front curve
  '<path d="M10 4 Q20 -2 30 4" fill="FILL"/>',
  // 3D body gradient overlay
  '<rect x="8" y="4" width="24" height="32" rx="4" fill="url(#mh-body)"/>',
  // Top edge highlight
  '<rect x="9" y="4" width="22" height="1" rx="0.5" fill="rgba(255,255,255,0.4)"/>',
  // Windshield (large, with glass gradient)
  '<rect x="11" y="6" width="18" height="7" rx="2.5" fill="url(#mh-glass)"/>',
  // Windshield reflection streak
  '<rect x="13" y="7" width="6" height="1" rx="0.5" fill="rgba(255,255,255,0.5)" transform="rotate(-8 16 7.5)"/>',
  // Body side stripe (chrome)
  '<rect x="10" y="16" width="20" height="1.5" rx="0.75" fill="rgba(255,255,255,0.3)"/>',
  // Side windows
  '<rect x="11" y="19" width="8" height="4" rx="1.2" fill="url(#mh-glass)" opacity="0.7"/>',
  '<rect x="21" y="19" width="8" height="4" rx="1.2" fill="url(#mh-glass)" opacity="0.6"/>',
  // Roof AC unit (raised)
  '<rect x="14" y="25" width="12" height="4" rx="1.5" fill="rgba(200,200,200,0.3)"/>',
  '<rect x="15" y="26" width="10" height="2" rx="1" fill="rgba(0,0,0,0.1)"/>',
  // Rear bumper
  '<rect x="10" y="34" width="20" height="2" rx="1" fill="rgba(0,0,0,0.35)"/>',
  // Tail lights
  '<rect x="10" y="33" width="3" height="2" rx="0.5" fill="rgba(220,50,50,0.6)"/>',
  '<rect x="27" y="33" width="3" height="2" rx="0.5" fill="rgba(220,50,50,0.5)"/>',
  // Wheels with rim detail
  '<ellipse cx="11" cy="10" rx="2.5" ry="3" fill="url(#mh-wheel)"/>',
  '<ellipse cx="11" cy="10" rx="1" ry="1.2" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="29" cy="10" rx="2.5" ry="3" fill="url(#mh-wheel)"/>',
  '<ellipse cx="29" cy="10" rx="1" ry="1.2" fill="rgba(180,180,180,0.4)"/>',
  '<ellipse cx="11" cy="32" rx="2.5" ry="3" fill="url(#mh-wheel)"/>',
  '<ellipse cx="11" cy="32" rx="1" ry="1.2" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="29" cy="32" rx="2.5" ry="3" fill="url(#mh-wheel)"/>',
  '<ellipse cx="29" cy="32" rx="1" ry="1.2" fill="rgba(180,180,180,0.4)"/>',
  // Left edge highlight line
  '<rect x="8" y="6" width="0.8" height="28" rx="0.4" fill="rgba(255,255,255,0.35)"/>',
].join('');

// ── Campervan: Rounded van shape, compact ───────────────────────────
const CAMPERVAN_SVG = [
  '<defs>',
  '<linearGradient id="cv-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
  '</linearGradient>',
  '<linearGradient id="cv-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.9"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.7"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.85"/>',
  '</linearGradient>',
  '<radialGradient id="cv-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#222"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow
  '<ellipse cx="21" cy="35" rx="11" ry="3" fill="rgba(0,0,0,0.3)"/>',
  // Main body (rounded)
  '<rect x="9" y="6" width="22" height="28" rx="7" fill="FILL"/>',
  // Cab front curve
  '<path d="M12 6 Q20 0 28 6" fill="FILL"/>',
  // Body gradient
  '<rect x="9" y="6" width="22" height="28" rx="7" fill="url(#cv-body)"/>',
  // Top edge highlight
  '<rect x="12" y="6" width="16" height="1" rx="0.5" fill="rgba(255,255,255,0.4)"/>',
  // Windshield
  '<rect x="12" y="8" width="16" height="6" rx="2.5" fill="url(#cv-glass)"/>',
  // Windshield reflection
  '<rect x="14" y="9" width="5" height="1" rx="0.5" fill="rgba(255,255,255,0.5)" transform="rotate(-8 16 9.5)"/>',
  // Pop-top line (chrome)
  '<rect x="11" y="17" width="18" height="1.2" rx="0.6" fill="rgba(255,255,255,0.35)"/>',
  // Side windows
  '<rect x="12" y="19" width="7" height="4" rx="1.2" fill="url(#cv-glass)" opacity="0.65"/>',
  '<rect x="21" y="19" width="7" height="4" rx="1.2" fill="url(#cv-glass)" opacity="0.55"/>',
  // Sliding door track
  '<rect x="22" y="17" width="0.7" height="13" rx="0.35" fill="rgba(0,0,0,0.15)"/>',
  // Rear bumper
  '<rect x="11" y="32" width="18" height="2" rx="0.75" fill="rgba(0,0,0,0.3)"/>',
  // Tail lights
  '<rect x="11" y="31" width="2.5" height="2" rx="0.5" fill="rgba(220,50,50,0.6)"/>',
  '<rect x="26.5" y="31" width="2.5" height="2" rx="0.5" fill="rgba(220,50,50,0.5)"/>',
  // Wheels
  '<ellipse cx="12" cy="12" rx="2.2" ry="2.8" fill="url(#cv-wheel)"/>',
  '<ellipse cx="12" cy="12" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="28" cy="12" rx="2.2" ry="2.8" fill="url(#cv-wheel)"/>',
  '<ellipse cx="28" cy="12" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.4)"/>',
  '<ellipse cx="12" cy="30" rx="2.2" ry="2.8" fill="url(#cv-wheel)"/>',
  '<ellipse cx="12" cy="30" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="28" cy="30" rx="2.2" ry="2.8" fill="url(#cv-wheel)"/>',
  '<ellipse cx="28" cy="30" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.4)"/>',
  // Left edge highlight
  '<rect x="9" y="8" width="0.8" height="24" rx="0.4" fill="rgba(255,255,255,0.35)"/>',
].join('');

// ── Caravan: Boxy trailer with hitch arm ─────────────────────────────
const CARAVAN_SVG = [
  '<defs>',
  '<linearGradient id="cr-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
  '</linearGradient>',
  '<linearGradient id="cr-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.85"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.65"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.8"/>',
  '</linearGradient>',
  '<radialGradient id="cr-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#222"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow
  '<ellipse cx="21" cy="34" rx="11" ry="3" fill="rgba(0,0,0,0.3)"/>',
  // Hitch arm
  '<rect x="19" y="1" width="2.5" height="7" rx="0.6" fill="#4b5563"/>',
  '<circle cx="20.25" cy="1.5" r="1.8" fill="#6b7280"/>',
  '<circle cx="20.25" cy="1.5" r="0.8" fill="#9ca3af"/>',
  // Main body
  '<rect x="9" y="8" width="22" height="25" rx="3" fill="FILL"/>',
  // Body gradient
  '<rect x="9" y="8" width="22" height="25" rx="3" fill="url(#cr-body)"/>',
  // Top edge highlight
  '<rect x="10" y="8" width="20" height="1" rx="0.5" fill="rgba(255,255,255,0.4)"/>',
  // Front window
  '<rect x="12" y="10" width="16" height="6" rx="2" fill="url(#cr-glass)"/>',
  // Window reflection
  '<rect x="14" y="11" width="5" height="1" rx="0.5" fill="rgba(255,255,255,0.45)" transform="rotate(-8 16 11.5)"/>',
  // Body stripe (chrome)
  '<rect x="11" y="18" width="18" height="1.2" rx="0.6" fill="rgba(255,255,255,0.3)"/>',
  // Side windows
  '<rect x="12" y="20" width="7" height="4" rx="1.2" fill="url(#cr-glass)" opacity="0.6"/>',
  '<rect x="21" y="20" width="7" height="4" rx="1.2" fill="url(#cr-glass)" opacity="0.5"/>',
  // Door
  '<rect x="17" y="26" width="5" height="5" rx="0.5" fill="rgba(0,0,0,0.08)"/>',
  '<circle cx="21" cy="28.5" r="0.6" fill="rgba(0,0,0,0.2)"/>',
  // Rear bumper
  '<rect x="11" y="31" width="18" height="2" rx="0.6" fill="rgba(0,0,0,0.3)"/>',
  // Tail lights
  '<rect x="10" y="30.5" width="2.5" height="2" rx="0.5" fill="rgba(220,50,50,0.6)"/>',
  '<rect x="27.5" y="30.5" width="2.5" height="2" rx="0.5" fill="rgba(220,50,50,0.5)"/>',
  // Wheels (single axle)
  '<ellipse cx="12" cy="30" rx="2.5" ry="3" fill="url(#cr-wheel)"/>',
  '<ellipse cx="12" cy="30" rx="1" ry="1.2" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="28" cy="30" rx="2.5" ry="3" fill="url(#cr-wheel)"/>',
  '<ellipse cx="28" cy="30" rx="1" ry="1.2" fill="rgba(180,180,180,0.4)"/>',
  // Left edge highlight
  '<rect x="9" y="10" width="0.8" height="21" rx="0.4" fill="rgba(255,255,255,0.35)"/>',
].join('');

// ── Camper Trailer: A-frame / folding tent trailer ──────────────────
const CAMPER_TRAILER_SVG = [
  '<defs>',
  '<linearGradient id="ct-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
  '</linearGradient>',
  '<linearGradient id="ct-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.85"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.65"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.8"/>',
  '</linearGradient>',
  '<radialGradient id="ct-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#222"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow
  '<ellipse cx="21" cy="32" rx="10" ry="2.5" fill="rgba(0,0,0,0.3)"/>',
  // Hitch arm
  '<rect x="19" y="1" width="2.5" height="7" rx="0.6" fill="#4b5563"/>',
  '<circle cx="20.25" cy="1.5" r="1.8" fill="#6b7280"/>',
  '<circle cx="20.25" cy="1.5" r="0.8" fill="#9ca3af"/>',
  // Main body
  '<rect x="10" y="12" width="20" height="18" rx="2.5" fill="FILL"/>',
  // A-frame roof
  '<path d="M11 12 L20 4 L29 12" fill="FILL"/>',
  // Roof left highlight
  '<path d="M11 12 L20 4 L20 12 Z" fill="rgba(255,255,255,0.2)"/>',
  // Roof right shadow
  '<path d="M20 4 L29 12 L20 12 Z" fill="rgba(0,0,0,0.15)"/>',
  // Body gradient
  '<rect x="10" y="12" width="20" height="18" rx="2.5" fill="url(#ct-body)"/>',
  // Canvas/front window
  '<rect x="14" y="13" width="12" height="5" rx="1.2" fill="url(#ct-glass)" opacity="0.7"/>',
  // Body detail stripe
  '<rect x="12" y="20" width="16" height="1" rx="0.5" fill="rgba(255,255,255,0.25)"/>',
  // Stabilizer legs
  '<rect x="11" y="27" width="1.8" height="3.5" rx="0.4" fill="rgba(80,80,80,0.4)"/>',
  '<rect x="27.2" y="27" width="1.8" height="3.5" rx="0.4" fill="rgba(80,80,80,0.35)"/>',
  // Wheels
  '<ellipse cx="13" cy="28" rx="2.2" ry="2.5" fill="url(#ct-wheel)"/>',
  '<ellipse cx="13" cy="28" rx="0.9" ry="1" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="27" cy="28" rx="2.2" ry="2.5" fill="url(#ct-wheel)"/>',
  '<ellipse cx="27" cy="28" rx="0.9" ry="1" fill="rgba(180,180,180,0.4)"/>',
  // Left edge highlight
  '<rect x="10" y="13" width="0.8" height="16" rx="0.4" fill="rgba(255,255,255,0.3)"/>',
].join('');

// ── Rooftent: Car/SUV with rooftop tent ──────────────────────────────
const ROOFTENT_SVG = [
  '<defs>',
  '<linearGradient id="rt-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
  '</linearGradient>',
  '<linearGradient id="rt-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.9"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.7"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.85"/>',
  '</linearGradient>',
  '<radialGradient id="rt-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#222"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow
  '<ellipse cx="21" cy="36" rx="10" ry="2.5" fill="rgba(0,0,0,0.3)"/>',
  // Car body (SUV base)
  '<rect x="10" y="16" width="20" height="18" rx="4" fill="FILL"/>',
  '<rect x="10" y="16" width="20" height="18" rx="4" fill="url(#rt-body)"/>',
  // Car windshield
  '<path d="M13 16 Q20 10 27 16" fill="url(#rt-glass)"/>',
  // Car side windows
  '<rect x="12" y="20" width="6" height="3" rx="1" fill="url(#rt-glass)" opacity="0.6"/>',
  '<rect x="22" y="20" width="6" height="3" rx="1" fill="url(#rt-glass)" opacity="0.5"/>',
  // Rear bumper
  '<rect x="12" y="32" width="16" height="2" rx="0.6" fill="rgba(0,0,0,0.3)"/>',
  // Tail lights
  '<rect x="11" y="31.5" width="2.5" height="2" rx="0.5" fill="rgba(220,50,50,0.6)"/>',
  '<rect x="26.5" y="31.5" width="2.5" height="2" rx="0.5" fill="rgba(220,50,50,0.5)"/>',
  // Car wheels
  '<ellipse cx="13" cy="19" rx="2.2" ry="2.5" fill="url(#rt-wheel)"/>',
  '<ellipse cx="13" cy="19" rx="0.9" ry="1" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="27" cy="19" rx="2.2" ry="2.5" fill="url(#rt-wheel)"/>',
  '<ellipse cx="27" cy="19" rx="0.9" ry="1" fill="rgba(180,180,180,0.4)"/>',
  '<ellipse cx="13" cy="31" rx="2.2" ry="2.5" fill="url(#rt-wheel)"/>',
  '<ellipse cx="13" cy="31" rx="0.9" ry="1" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="27" cy="31" rx="2.2" ry="2.5" fill="url(#rt-wheel)"/>',
  '<ellipse cx="27" cy="31" rx="0.9" ry="1" fill="rgba(180,180,180,0.4)"/>',
  // Rooftent (deployed, wider than car)
  '<rect x="8" y="3" width="24" height="13" rx="2.5" fill="FILL" opacity="0.9"/>',
  '<rect x="8" y="3" width="24" height="13" rx="2.5" fill="url(#rt-body)"/>',
  // Tent top highlight
  '<rect x="9" y="3" width="22" height="1" rx="0.5" fill="rgba(255,255,255,0.4)"/>',
  // Tent window
  '<rect x="13" y="5" width="14" height="5" rx="1.2" fill="url(#rt-glass)" opacity="0.5"/>',
  // Tent-to-car ladder
  '<rect x="19" y="14.5" width="2" height="2" rx="0.3" fill="rgba(80,80,80,0.25)"/>',
  // Left edge highlights
  '<rect x="8" y="5" width="0.8" height="10" rx="0.4" fill="rgba(255,255,255,0.35)"/>',
  '<rect x="10" y="18" width="0.8" height="14" rx="0.4" fill="rgba(255,255,255,0.3)"/>',
].join('');

// ── Offroad: Rugged 4x4 with roof rack and bull bar ─────────────────
const OFFROAD_SVG = [
  '<defs>',
  '<linearGradient id="or-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
  '</linearGradient>',
  '<linearGradient id="or-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.9"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.7"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.85"/>',
  '</linearGradient>',
  '<radialGradient id="or-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#333"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow
  '<ellipse cx="21" cy="36" rx="11" ry="3" fill="rgba(0,0,0,0.3)"/>',
  // Main body (boxy SUV)
  '<rect x="9" y="7" width="22" height="28" rx="3" fill="FILL"/>',
  '<rect x="9" y="7" width="22" height="28" rx="3" fill="url(#or-body)"/>',
  // Front bumper / bull bar
  '<rect x="11" y="5" width="18" height="3.5" rx="1" fill="rgba(60,60,60,0.5)"/>',
  // Headlights on bull bar
  '<rect x="13" y="5.5" width="5" height="2.5" rx="0.8" fill="rgba(255,255,200,0.45)"/>',
  '<rect x="22" y="5.5" width="5" height="2.5" rx="0.8" fill="rgba(255,255,200,0.4)"/>',
  // Top edge highlight
  '<rect x="10" y="7" width="20" height="1" rx="0.5" fill="rgba(255,255,255,0.35)"/>',
  // Windshield
  '<rect x="12" y="10" width="16" height="6" rx="2" fill="url(#or-glass)"/>',
  // Windshield reflection
  '<rect x="14" y="11" width="5" height="1" rx="0.5" fill="rgba(255,255,255,0.45)" transform="rotate(-8 16 11.5)"/>',
  // Roof rack (metal bars)
  '<rect x="11" y="19" width="18" height="9" rx="1" fill="rgba(80,80,80,0.15)"/>',
  '<rect x="12" y="20" width="16" height="0.7" rx="0.35" fill="rgba(80,80,80,0.25)"/>',
  '<rect x="12" y="24" width="16" height="0.7" rx="0.35" fill="rgba(80,80,80,0.25)"/>',
  // Spare tire outline on roof
  '<circle cx="20" cy="22" r="3" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="0.7"/>',
  // Rear details
  '<rect x="13" y="30" width="14" height="3" rx="1" fill="rgba(0,0,0,0.1)"/>',
  '<rect x="10" y="33" width="20" height="2" rx="1" fill="rgba(0,0,0,0.3)"/>',
  // Tail lights
  '<rect x="10" y="32" width="3" height="2" rx="0.5" fill="rgba(220,50,50,0.6)"/>',
  '<rect x="27" y="32" width="3" height="2" rx="0.5" fill="rgba(220,50,50,0.5)"/>',
  // Big off-road wheels (chunky tires with visible rims)
  '<ellipse cx="11" cy="13" rx="3" ry="3.5" fill="url(#or-wheel)"/>',
  '<ellipse cx="11" cy="13" rx="1.2" ry="1.4" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="29" cy="13" rx="3" ry="3.5" fill="url(#or-wheel)"/>',
  '<ellipse cx="29" cy="13" rx="1.2" ry="1.4" fill="rgba(180,180,180,0.4)"/>',
  '<ellipse cx="11" cy="31" rx="3" ry="3.5" fill="url(#or-wheel)"/>',
  '<ellipse cx="11" cy="31" rx="1.2" ry="1.4" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="29" cy="31" rx="3" ry="3.5" fill="url(#or-wheel)"/>',
  '<ellipse cx="29" cy="31" rx="1.2" ry="1.4" fill="rgba(180,180,180,0.4)"/>',
  // Left edge highlight
  '<rect x="9" y="9" width="0.8" height="24" rx="0.4" fill="rgba(255,255,255,0.35)"/>',
].join('');

// ── Car: Standard sedan / hatchback looking vehicle ──────────────────
const CAR_SVG = [
  '<defs>',
  '<linearGradient id="cr-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.2"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.2"/>',
  '</linearGradient>',
  '<linearGradient id="cr-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.9"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.7"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.85"/>',
  '</linearGradient>',
  '<radialGradient id="cr-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#222"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow
  '<ellipse cx="21" cy="34" rx="10" ry="2.5" fill="rgba(0,0,0,0.3)"/>',
  // Main body
  '<rect x="10" y="8" width="20" height="24" rx="5" fill="FILL"/>',
  '<rect x="10" y="8" width="20" height="24" rx="5" fill="url(#cr-body)"/>',
  // Windshield
  '<rect x="13" y="10" width="14" height="6" rx="2" fill="url(#cr-glass)"/>',
  // Side windows
  '<rect x="11.5" y="18" width="6" height="4" rx="1.2" fill="url(#cr-glass)" opacity="0.6"/>',
  '<rect x="22.5" y="18" width="6" height="4" rx="1.2" fill="url(#cr-glass)" opacity="0.5"/>',
  // Rear bumper
  '<rect x="11" y="30" width="18" height="2" rx="0.6" fill="rgba(0,0,0,0.3)"/>',
  // Tail lights
  '<rect x="11" y="29.5" width="2.5" height="1.8" rx="0.4" fill="rgba(220,50,50,0.6)"/>',
  '<rect x="26.5" y="29.5" width="2.5" height="1.8" rx="0.4" fill="rgba(220,50,50,0.5)"/>',
  // Wheels
  '<ellipse cx="12.5" cy="13" rx="2.2" ry="2.8" fill="url(#cr-wheel)"/>',
  '<ellipse cx="12.5" cy="13" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="27.5" cy="13" rx="2.2" ry="2.8" fill="url(#cr-wheel)"/>',
  '<ellipse cx="27.5" cy="13" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.4)"/>',
  '<ellipse cx="12.5" cy="28" rx="2.2" ry="2.8" fill="url(#cr-wheel)"/>',
  '<ellipse cx="12.5" cy="28" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.5)"/>',
  '<ellipse cx="27.5" cy="28" rx="2.2" ry="2.8" fill="url(#cr-wheel)"/>',
  '<ellipse cx="27.5" cy="28" rx="0.9" ry="1.1" fill="rgba(180,180,180,0.4)"/>',
  // Left edge highlight
  '<rect x="10" y="10" width="0.8" height="20" rx="0.4" fill="rgba(255,255,255,0.3)"/>',
].join('');

// ── Category → avatar map ───────────────────────────────────────────
const VEHICLE_AVATARS: Record<string, string> = {
  motorhome: MOTORHOME_SVG,
  campervan: CAMPERVAN_SVG,
  caravan: CARAVAN_SVG,
  camper_trailer: CAMPER_TRAILER_SVG,
  rooftent: ROOFTENT_SVG,
  offroad: OFFROAD_SVG,
  car: CAR_SVG,
};

/**
 * Get the 3D SVG avatar string for a vehicle category.
 * Returns SVG inner elements with "FILL" as color placeholder.
 */
export function getVehicleAvatar(category: VehicleCategory): string {
  return VEHICLE_AVATARS[category] || VEHICLE_AVATARS.motorhome;
}

/**
 * Get the 3D SVG avatar with color applied and wrapped in an SVG element.
 */
export function getVehicleAvatarSvg(
  category: VehicleCategory,
  color: string,
  size = 40,
): string {
  const avatar = getVehicleAvatar(category);
  const colored = avatar.replace(/FILL/g, color);
  return `<svg viewBox="0 0 40 40" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">${colored}</svg>`;
}

// ── Tow vehicle (SUV) avatar for vogntog ────────────────────────────
const TOW_VEHICLE_AVATAR = [
  '<defs>',
  '<linearGradient id="tw-body" x1="0" y1="0" x2="1" y2="0">',
  '<stop offset="0%" stop-color="white" stop-opacity="0.2"/>',
  '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
  '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
  '<stop offset="100%" stop-color="black" stop-opacity="0.2"/>',
  '</linearGradient>',
  '<linearGradient id="tw-glass" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.9"/>',
  '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.7"/>',
  '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.85"/>',
  '</linearGradient>',
  '<radialGradient id="tw-wheel" cx="0.4" cy="0.35">',
  '<stop offset="0%" stop-color="#555"/>',
  '<stop offset="50%" stop-color="#222"/>',
  '<stop offset="100%" stop-color="#111"/>',
  '</radialGradient>',
  '</defs>',
  // Ground shadow
  '<ellipse cx="21" cy="22" rx="10" ry="2.5" fill="rgba(0,0,0,0.3)"/>',
  // Body
  '<rect x="11" y="1" width="18" height="19" rx="4" fill="#475569"/>',
  '<path d="M13 1 Q20 -4 27 1" fill="#475569"/>',
  // Body gradient
  '<rect x="11" y="1" width="18" height="19" rx="4" fill="url(#tw-body)"/>',
  // Top highlight
  '<rect x="12" y="1" width="16" height="0.8" rx="0.4" fill="rgba(255,255,255,0.35)"/>',
  // Windshield
  '<rect x="14" y="2.5" width="12" height="4.5" rx="1.5" fill="url(#tw-glass)"/>',
  // Windshield reflection
  '<rect x="15.5" y="3" width="4" height="0.8" rx="0.4" fill="rgba(255,255,255,0.4)" transform="rotate(-8 17 3.4)"/>',
  // Side windows
  '<rect x="13.5" y="9" width="5.5" height="3" rx="1" fill="url(#tw-glass)" opacity="0.5"/>',
  '<rect x="21" y="9" width="5.5" height="3" rx="1" fill="url(#tw-glass)" opacity="0.4"/>',
  // Rear bumper
  '<rect x="12" y="18.5" width="16" height="1.5" rx="0.75" fill="rgba(0,0,0,0.3)"/>',
  // Tail lights
  '<rect x="12" y="18" width="2" height="1.5" rx="0.4" fill="rgba(220,50,50,0.5)"/>',
  '<rect x="26" y="18" width="2" height="1.5" rx="0.4" fill="rgba(220,50,50,0.4)"/>',
  // Wheels
  '<ellipse cx="13.5" cy="5" rx="2" ry="2.5" fill="url(#tw-wheel)"/>',
  '<ellipse cx="13.5" cy="5" rx="0.8" ry="1" fill="rgba(180,180,180,0.45)"/>',
  '<ellipse cx="26.5" cy="5" rx="2" ry="2.5" fill="url(#tw-wheel)"/>',
  '<ellipse cx="26.5" cy="5" rx="0.8" ry="1" fill="rgba(180,180,180,0.4)"/>',
  '<ellipse cx="13.5" cy="17" rx="2" ry="2.5" fill="url(#tw-wheel)"/>',
  '<ellipse cx="13.5" cy="17" rx="0.8" ry="1" fill="rgba(180,180,180,0.45)"/>',
  '<ellipse cx="26.5" cy="17" rx="2" ry="2.5" fill="url(#tw-wheel)"/>',
  '<ellipse cx="26.5" cy="17" rx="0.8" ry="1" fill="rgba(180,180,180,0.4)"/>',
  // Left edge highlight
  '<rect x="11" y="3" width="0.7" height="16" rx="0.35" fill="rgba(255,255,255,0.3)"/>',
].join('');

export function getTowVehicleAvatar(): string {
  return TOW_VEHICLE_AVATAR;
}

export function getTowVehicleAvatarSvg(size = 32): string {
  return `<svg viewBox="0 0 40 24" width="${size}" height="${Math.round(size * 0.6)}" xmlns="http://www.w3.org/2000/svg">${TOW_VEHICLE_AVATAR}</svg>`;
}
